(() => {
  const SELECTOR = '[data-ap-uk-preorder]';
  const KEY = '__apUKPreorder';

  const clean = (value) => String(value || '').trim();

  const parseJSON = (text) => {
    try {
      return JSON.parse(text);
    } catch (error) {
      console.error('[ALLPOWERS UK Pre-order] Invalid variant JSON:', error);
      return [];
    }
  };

  const parseDate = (value) => {
    if (!value) return null;
    const [year, month, day] = String(value).split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(Date.UTC(year, month - 1, day));
  };

  const ukDate = (date, short = false) => {
    if (!date) return '';
    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: short ? 'short' : 'long',
      year: short ? undefined : 'numeric',
      timeZone: 'UTC'
    }).format(date);
  };

  const formatRange = (startValue, endValue, fallback = '') => {
    const start = parseDate(startValue);
    const end = parseDate(endValue);

    if (start && end) {
      const sameYear = start.getUTCFullYear() === end.getUTCFullYear();
      const sameMonth = sameYear && start.getUTCMonth() === end.getUTCMonth();

      if (sameMonth) {
        const month = new Intl.DateTimeFormat('en-GB', {
          month: 'long', timeZone: 'UTC'
        }).format(start);
        return `${start.getUTCDate()}–${end.getUTCDate()} ${month} ${end.getUTCFullYear()}`;
      }

      return `${ukDate(start)}–${ukDate(end)}`;
    }

    if (end) return `By ${ukDate(end)}`;
    if (start) return `From ${ukDate(start)}`;
    return fallback || 'Estimated dispatch date will be confirmed soon.';
  };

  const formatBadgeRange = (startValue, endValue) => {
    const start = parseDate(startValue);
    const end = parseDate(endValue);

    if (start && end) {
      const sameYear = start.getUTCFullYear() === end.getUTCFullYear();
      const sameMonth = sameYear && start.getUTCMonth() === end.getUTCMonth();

      if (sameMonth) {
        const month = new Intl.DateTimeFormat('en-GB', {
          month: 'short', timeZone: 'UTC'
        }).format(start);
        return `${start.getUTCDate()}–${end.getUTCDate()} ${month}`;
      }

      return `${ukDate(start, true)}–${ukDate(end, true)}`;
    }

    if (end) return `By ${ukDate(end, true)}`;
    if (start) return `From ${ukDate(start, true)}`;
    return '';
  };

  const earliest = (values) => {
    const valid = values.map((value) => ({ value, date: parseDate(value) })).filter((item) => item.date);
    if (!valid.length) return null;
    valid.sort((a, b) => a.date - b.date);
    return valid[0].value;
  };

  const latest = (values) => {
    const valid = values.map((value) => ({ value, date: parseDate(value) })).filter((item) => item.date);
    if (!valid.length) return null;
    valid.sort((a, b) => b.date - a.date);
    return valid[0].value;
  };

  const splitLabels = (value) => {
    const labels = clean(value).split('|').map((item) => item.trim()).filter(Boolean);
    return { first: labels[0] || '', second: labels[1] || '' };
  };

  const buildDateLabel = (name) => clean(name)
    ? `${clean(name)} Estimated Dispatch Date`
    : 'Estimated Dispatch Date';

  const isBundle = (productTitle, variantTitle) => {
    const text = `${clean(productTitle)} ${clean(variantTitle)}`.toLowerCase();
    return text.includes('+') || text.includes('bundle') || text.includes('kit') || text.includes('generator');
  };

  const joinNames = (first, second) => {
    const names = [clean(first), clean(second)].filter(Boolean);
    if (names.length === 2) return `${names[0]} and ${names[1]}`;
    return names[0] || '';
  };

  const automaticNote = ({ first, second, productTitle, variantTitle }) => {
    const names = joinNames(first, second);
    if (!names) return 'Reserve yours now. Your order will be dispatched once stock arrives.';

    const plural = Boolean(clean(first) && clean(second));
    if (isBundle(productTitle, variantTitle)) {
      return plural
        ? `Only the ${names} in this bundle are on pre-order. In-stock items may be dispatched separately and arrive earlier.`
        : `Only the ${names} in this bundle is on pre-order. In-stock items may be dispatched separately and arrive earlier.`;
    }

    return plural
      ? `${names} are on pre-order. Your order will be dispatched once stock arrives.`
      : `${names} is on pre-order. Your order will be dispatched once stock arrives.`;
  };

  const shippingData = (variant, fallback) => {
    const labels = splitLabels(variant.preorderItemLabel);
    const general = { start: variant.dispatchStart, end: variant.dispatchEnd, text: formatRange(variant.dispatchStart, variant.dispatchEnd, fallback) };
    const firstItem = { start: variant.firstItemStart, end: variant.firstItemEnd, text: formatRange(variant.firstItemStart, variant.firstItemEnd, fallback) };
    const secondItem = { start: variant.secondItemStart, end: variant.secondItemEnd, text: formatRange(variant.secondItemStart, variant.secondItemEnd, fallback) };
    const hasFirst = Boolean(firstItem.start || firstItem.end);
    const hasSecond = Boolean(secondItem.start || secondItem.end);

    if (hasFirst && !hasSecond) {
      return { showGeneral: false, showFirst: true, showSecond: false, general, firstItem, secondItem, firstLabel: labels.first || 'Pre-order Item', secondLabel: '', badgeStart: firstItem.start, badgeEnd: firstItem.end };
    }

    if (!hasFirst && hasSecond) {
      return { showGeneral: false, showFirst: true, showSecond: false, general, firstItem: secondItem, secondItem, firstLabel: labels.first || 'Pre-order Item', secondLabel: '', badgeStart: secondItem.start, badgeEnd: secondItem.end };
    }

    if (hasFirst && hasSecond) {
      return {
        showGeneral: false, showFirst: true, showSecond: true, general, firstItem, secondItem,
        firstLabel: labels.first || 'First Pre-order Item',
        secondLabel: labels.second || 'Second Pre-order Item',
        badgeStart: earliest([firstItem.start, firstItem.end, secondItem.start, secondItem.end]),
        badgeEnd: latest([firstItem.start, firstItem.end, secondItem.start, secondItem.end])
      };
    }

    return { showGeneral: true, showFirst: false, showSecond: false, general, firstItem, secondItem, generalLabel: labels.first || '', firstLabel: '', secondLabel: '', badgeStart: general.start, badgeEnd: general.end };
  };

  class APUKPreorder {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.section = root.closest('.shopify-section') || document.getElementById(`shopify-section-${this.sectionId}`) || root.closest('[data-ui-component="Main Product Section"]');
      this.variants = parseJSON(root.querySelector('[data-ap-uk-preorder-variants]')?.textContent || '[]');
      this.map = new Map(this.variants.map((variant) => [String(variant.id), variant]));
      this.cleanup = [];
      this.timer = null;
      if (!this.section) return;
      this.find();
      this.place();
      this.observe();
      this.renderSoon();
      this.scheduleExpiry();
    }

    find() {
      this.form = this.section.querySelector('.js-main-product-form');
      this.idInput = this.form?.querySelector('input[name="id"]');
      this.gallery = this.section.querySelector('.product__media-wrapper--single-with-thumbnails, .product-slideshow__slider, .product__media-wrapper');
      this.info = this.section.querySelector('.main-product-info-container');
      this.inventoryBlock = this.section.querySelector('[id^="inventoryTracking-"]');
      this.buyBlock = this.section.querySelector('.product__form');
      this.atcButton = this.section.querySelector('.js-product-form-submit-btn');
      this.atcText = this.section.querySelector('.js-product-form-submit-btn-text');
      this.dynamicCheckout = this.section.querySelector('sht-load-dyn-co-btn');
      this.badge = this.root.querySelector('[data-ap-uk-po-badge]');
      this.badgeItem = this.root.querySelector('[data-ap-uk-po-badge-item]');
      this.inventoryUI = this.root.querySelector('[data-ap-uk-po-inventory]');
      this.card = this.root.querySelector('[data-ap-uk-po-card]');
      this.generalRow = this.root.querySelector('[data-ap-uk-po-general-shipping]');
      this.generalLabel = this.root.querySelector('[data-ap-uk-po-general-label]');
      this.generalDate = this.root.querySelector('[data-ap-uk-po-date]');
      this.firstRow = this.root.querySelector('[data-ap-uk-po-first-item-shipping]');
      this.firstLabel = this.root.querySelector('[data-ap-uk-po-first-item-label]');
      this.firstDate = this.root.querySelector('[data-ap-uk-po-first-item-date]');
      this.secondRow = this.root.querySelector('[data-ap-uk-po-second-item-shipping]');
      this.secondLabel = this.root.querySelector('[data-ap-uk-po-second-item-label]');
      this.secondDate = this.root.querySelector('[data-ap-uk-po-second-item-date]');
    }

    place() {
      if (this.gallery && this.badge) {
        if (getComputedStyle(this.gallery).position === 'static') this.gallery.style.position = 'relative';
        this.gallery.appendChild(this.badge);
      }

      if (this.inventoryBlock && this.inventoryUI) {
        this.inventoryBlock.insertAdjacentElement('afterend', this.inventoryUI);
      } else if (this.info && this.inventoryUI) {
        this.info.appendChild(this.inventoryUI);
      }

      if (this.buyBlock && this.card) {
        this.buyBlock.insertAdjacentElement('afterend', this.card);
      } else if (this.info && this.card) {
        this.info.appendChild(this.card);
      }
    }

    currentVariantId() {
      if (this.idInput?.value) return String(this.idInput.value);
      const fromUrl = new URL(window.location.href).searchParams.get('variant');
      if (fromUrl) return String(fromUrl);
      return String(this.variants[0]?.id || '');
    }

    currentVariant() {
      return this.map.get(this.currentVariantId()) || this.variants[0] || null;
    }

    active(variant) {
      if (!variant?.preorderEnabled) return false;
      if (!Number(variant.expiresEpoch)) return true;
      return Math.floor(Date.now() / 1000) < Number(variant.expiresEpoch);
    }

    clearProperties() {
      this.form?.querySelectorAll('[data-ap-uk-po-property]').forEach((element) => element.remove());
    }

    addProperty(name, value) {
      if (!this.form || value === '' || value == null) return;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${name}]`;
      input.value = String(value);
      input.dataset.apUkPoProperty = 'true';
      this.form.appendChild(input);
    }

    hideRows() {
      [this.generalRow, this.firstRow, this.secondRow].forEach((row) => { if (row) row.hidden = true; });
      [this.generalDate, this.firstDate, this.secondDate].forEach((node) => { if (node) node.textContent = ''; });
    }

    reset(variant) {
      this.section.classList.remove('ap-uk-po-special');
      if (this.badge) this.badge.hidden = true;
      if (this.badgeItem) { this.badgeItem.textContent = ''; this.badgeItem.hidden = true; }
      if (this.card) this.card.hidden = true;
      if (this.inventoryUI) this.inventoryUI.hidden = true;
      if (this.inventoryBlock) this.inventoryBlock.hidden = false;
      this.hideRows();
      this.clearProperties();

      if (this.dynamicCheckout) this.dynamicCheckout.hidden = false;
      if (this.atcButton && variant) this.atcButton.disabled = !variant.available;
      if (this.atcText && variant) {
        this.atcText.textContent = variant.available
          ? (this.root.dataset.addToCartText || 'Add to cart')
          : (this.root.dataset.soldOutText || 'Sold out');
      }
    }

    applyPreorder(variant) {
      this.reset(variant);
      const data = shippingData(variant, this.root.dataset.noDateText);
      const firstName = data.showFirst ? data.firstLabel : data.generalLabel;
      const secondName = data.showSecond ? data.secondLabel : '';
      const note = automaticNote({ first: firstName, second: secondName, productTitle: this.root.dataset.productTitle, variantTitle: variant.title });
      const badgeRange = formatBadgeRange(data.badgeStart, data.badgeEnd);
      const badgeName = data.showSecond ? [data.firstLabel, data.secondLabel].filter(Boolean).join(' + ') : (data.showFirst ? data.firstLabel : data.generalLabel);

      if (this.root.dataset.showBadge === 'true' && this.badge) {
        this.badge.hidden = false;
        const title = this.badge.querySelector('[data-ap-uk-po-badge-title]');
        const date = this.badge.querySelector('[data-ap-uk-po-badge-date]');
        if (title) title.textContent = this.root.dataset.preorderTitle || 'PRE-ORDER';
        if (this.badgeItem) { this.badgeItem.textContent = badgeName || ''; this.badgeItem.hidden = !badgeName; }
        if (date) { date.textContent = badgeRange; date.hidden = !badgeRange; }
      }

      if (this.root.dataset.showCard === 'true' && this.card) {
        this.card.hidden = false;
        const title = this.card.querySelector('[data-ap-uk-po-card-title]');
        if (title) title.textContent = this.root.dataset.preorderTitle || 'PRE-ORDER';

        if (data.showGeneral && this.generalRow) {
          this.generalRow.hidden = false;
          if (this.generalLabel) this.generalLabel.textContent = buildDateLabel(data.generalLabel);
          if (this.generalDate) this.generalDate.textContent = data.general.text;
        }

        if (data.showFirst && this.firstRow) {
          this.firstRow.hidden = false;
          if (this.firstLabel) this.firstLabel.textContent = buildDateLabel(data.firstLabel);
          if (this.firstDate) this.firstDate.textContent = data.firstItem.text;
        }

        if (data.showSecond && this.secondRow) {
          this.secondRow.hidden = false;
          if (this.secondLabel) this.secondLabel.textContent = buildDateLabel(data.secondLabel);
          if (this.secondDate) this.secondDate.textContent = data.secondItem.text;
        }

        const warehouse = this.card.querySelector('[data-ap-uk-po-warehouse]');
        const shortNote = this.card.querySelector('[data-ap-uk-po-short-note]');
        const detailsTitle = this.card.querySelector('[data-ap-uk-po-details-title]');
        const details = this.card.querySelector('[data-ap-uk-po-details]');
        const splitNote = this.card.querySelector('[data-ap-uk-po-split-note]');

        if (warehouse) warehouse.textContent = this.root.dataset.defaultWarehouse || 'UK Warehouse';
        if (shortNote) shortNote.textContent = note;
        if (detailsTitle) detailsTitle.textContent = this.root.dataset.detailsTitle || 'Dispatch Details';
        if (details) details.textContent = this.root.dataset.defaultDetails || '';
        if (splitNote) {
          const text = variant.splitShipping ? clean(this.root.dataset.splitNote) : '';
          splitNote.textContent = text;
          splitNote.hidden = !text;
        }
      }

      if (this.inventoryBlock) this.inventoryBlock.hidden = true;
      if (this.inventoryUI) {
        this.inventoryUI.hidden = false;
        this.inventoryUI.textContent = 'Pre-order available';
      }

      if (this.atcButton) this.atcButton.disabled = false;
      if (this.atcText) this.atcText.textContent = this.root.dataset.buttonText || 'PRE-ORDER NOW';

      if (this.root.dataset.hideDynamicCheckout === 'true') {
        this.section.classList.add('ap-uk-po-special');
        if (this.dynamicCheckout) this.dynamicCheckout.hidden = true;
      }

      this.addProperty('Pre-order', 'Yes');
      const itemNames = [data.firstLabel, data.secondLabel, data.generalLabel].filter(Boolean).join(' + ');
      if (itemNames) this.addProperty('Pre-order item', itemNames);
      if (data.showGeneral) this.addProperty(buildDateLabel(data.generalLabel), data.general.text);
      if (data.showFirst) this.addProperty(buildDateLabel(data.firstLabel), data.firstItem.text);
      if (data.showSecond) this.addProperty(buildDateLabel(data.secondLabel), data.secondItem.text);
      this.addProperty('Dispatch Warehouse', this.root.dataset.defaultWarehouse || 'UK Warehouse');
      if (variant.splitShipping) this.addProperty('Separate dispatches may apply', 'Yes');
    }

    render() {
      const variant = this.currentVariant();
      if (!variant) return;
      if (this.active(variant)) this.applyPreorder(variant);
      else this.reset(variant);
    }

    renderSoon() {
      requestAnimationFrame(() => setTimeout(() => this.render(), 120));
    }

    observe() {
      const listener = () => this.renderSoon();
      this.section.addEventListener('change', listener);
      this.section.addEventListener('click', listener);
      this.cleanup.push(() => {
        this.section.removeEventListener('change', listener);
        this.section.removeEventListener('click', listener);
      });

      this.observer = new MutationObserver(() => this.renderSoon());
      if (this.idInput) {
        this.observer.observe(this.idInput, { attributes: true, attributeFilter: ['value', 'disabled'] });
      }
      this.cleanup.push(() => this.observer.disconnect());
    }

    scheduleExpiry() {
      const now = Math.floor(Date.now() / 1000);
      const next = this.variants.map((variant) => Number(variant.expiresEpoch) || 0).filter((time) => time > now).sort((a, b) => a - b)[0];
      if (!next) return;
      const delay = Math.min(Math.max(next * 1000 - Date.now() + 250, 250), 2147483647);
      this.timer = setTimeout(() => this.render(), delay);
    }

    destroy() {
      this.cleanup.forEach((callback) => callback());
      if (this.timer) clearTimeout(this.timer);
      delete this.root[KEY];
    }
  }

  const init = (root) => {
    if (!root || root[KEY]) return;
    root[KEY] = new APUKPreorder(root);
  };

  const initAll = (scope = document) => scope.querySelectorAll(SELECTOR).forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(SELECTOR).forEach((root) => root[KEY]?.destroy());
  });
})();
